import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Plus, Save, Trash2, Undo2 } from 'lucide-react'
import {
  apiErrorMessage,
  fetchLotMaster,
  updateLotMaster,
} from '@/services/api'
import type { LotDef } from '@/types/machine'
import ConfirmDialog from '@/components/ConfirmDialog'
import MarkdownField from '@/components/MarkdownField'

const inputClass =
  'w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'

// 編集中の1行。originalName はサーバ上の現在名（改名検出に使う。新規追加は undefined）
interface EditableLot extends LotDef {
  id: string
  originalName?: string
}

// ロットマスタの編集画面。改名は保存時に全機材のロット表記へ一括反映される
// （削除はマスタから消えるだけで、機材に設定済みのロット値は変わらない）
export default function LotsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const masterQuery = useQuery({
    queryKey: ['lotMaster'],
    queryFn: fetchLotMaster,
  })

  // 編集中のマスタ（保存するまでサーバへは反映しない）
  const [lots, setLots] = useState<EditableLot[] | null>(null)
  const [newName, setNewName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // 行の React key 用のローカルID（名前は編集可能なので key に使えない）
  const idRef = useRef(0)
  const toEditable = (list: LotDef[]): EditableLot[] =>
    list.map((lot) => ({
      ...lot,
      id: `l${idRef.current++}`,
      originalName: lot.name,
    }))

  useEffect(() => {
    if (masterQuery.data && lots === null) {
      setLots(toEditable(masterQuery.data))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterQuery.data, lots])

  const saved = useMemo(() => masterQuery.data ?? [], [masterQuery.data])
  const dirty = useMemo(() => {
    if (!lots) return false
    const project = (list: LotDef[]) =>
      JSON.stringify(list.map((lot) => [lot.name, lot.note ?? '']))
    return (
      project(lots) !== project(saved) ||
      lots.some((lot) => lot.originalName && lot.originalName !== lot.name)
    )
  }, [lots, saved])

  // 改名（削除+追加ではなく、全機材のロット表記を付け替える）
  const renames = useMemo(
    () =>
      (lots ?? [])
        .filter(
          (lot) => lot.originalName && lot.originalName !== lot.name.trim()
        )
        .map((lot) => ({ from: lot.originalName!, to: lot.name.trim() })),
    [lots]
  )
  // 保存時にマスタから削除されるロット（機材側の値は残る旨を確認する）
  const removedNames = useMemo(() => {
    if (!lots) return []
    const keptOriginals = new Set(
      lots.map((lot) => lot.originalName).filter(Boolean)
    )
    return saved
      .filter((lot) => !keptOriginals.has(lot.name))
      .map((lot) => lot.name)
  }, [lots, saved])

  const saveMutation = useMutation({
    mutationFn: () =>
      updateLotMaster(
        lots!.map((lot) => ({ name: lot.name.trim(), note: lot.note ?? '' })),
        renames
      ),
    onSuccess: (result) => {
      setError(null)
      setSavedMessage(t('lotsPage.saved', { count: result.machinesUpdated }))
      setLots(toEditable(result.lots))
      queryClient.setQueryData(['lotMaster'], result.lots)
      // 改名は全機材のロット表記に反映されるため一覧・詳細のキャッシュも更新する
      queryClient.invalidateQueries({ queryKey: ['machines'] })
      queryClient.invalidateQueries({ queryKey: ['machine'] })
    },
    onError: (err) => setError(apiErrorMessage(err) ?? t('lotsPage.saveError')),
    onSettled: () => setConfirmOpen(false),
  })

  const handleAdd = () => {
    const name = newName.trim()
    if (!name || !lots) return
    if (lots.some((lot) => lot.name.trim() === name)) {
      setError(t('lotsPage.duplicate', { name }))
      return
    }
    setError(null)
    setSavedMessage(null)
    setLots([...lots, { id: `l${idRef.current++}`, name, note: '' }])
    setNewName('')
  }

  const handleChange = (id: string, patch: Partial<LotDef>) => {
    if (!lots) return
    setSavedMessage(null)
    setLots(lots.map((lot) => (lot.id === id ? { ...lot, ...patch } : lot)))
  }

  const handleRemove = (id: string) => {
    if (!lots) return
    setError(null)
    setSavedMessage(null)
    setLots(lots.filter((lot) => lot.id !== id))
  }

  const handleSave = () => {
    if (!lots) return
    // 空名・重複名は保存前に弾く
    const names = lots.map((lot) => lot.name.trim())
    if (names.some((n) => !n) || new Set(names).size !== names.length) {
      setError(t('lotsPage.invalidNames'))
      return
    }
    setError(null)
    if (removedNames.length > 0) {
      setConfirmOpen(true)
    } else {
      saveMutation.mutate()
    }
  }

  const handleBack = () => {
    if (dirty && !window.confirm(t('detail.confirmLeave'))) return
    navigate('/')
  }

  if (masterQuery.isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <p className="text-sm text-red-600">{t('lotsPage.fetchError')}</p>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </button>
      </div>
    )
  }

  if (masterQuery.isLoading || !lots) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <span className="ml-3 text-gray-500">{t('common.loading')}</span>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap shrink-0"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              if (!window.confirm(t('detail.undoConfirm'))) return
              setLots(toEditable(saved))
              setError(null)
            }}
            disabled={!dirty}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap shrink-0"
          >
            <Undo2 className="h-4 w-4" />
            {t('detail.undo')}
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || saveMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap shrink-0"
          >
            <Save className="h-4 w-4" />
            {saveMutation.isPending ? t('detail.saving') : t('detail.save')}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}
      {savedMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
          {savedMessage}
        </div>
      )}
      {dirty && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-700">
          {t('lotsPage.unsavedHint')}
        </div>
      )}

      <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">
          {t('lotsPage.title')}
        </h3>
        <p className="text-xs text-gray-500 mb-4">{t('lotsPage.hint')}</p>
        <div className="border border-gray-200 rounded-md overflow-hidden mb-3">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 w-48">
                  {t('lotsPage.name')}
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                  {t('lotsPage.note')}
                </th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lots.length === 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="px-4 py-3 text-sm text-gray-400 text-center"
                  >
                    {t('lotsPage.empty')}
                  </td>
                </tr>
              )}
              {lots.map((lot) => {
                const renamed =
                  lot.originalName && lot.originalName !== lot.name.trim()
                return (
                  <tr key={lot.id}>
                    <td className="px-2 py-1.5 align-top">
                      <input
                        type="text"
                        value={lot.name}
                        onChange={(e) =>
                          handleChange(lot.id, { name: e.target.value })
                        }
                        className="w-full px-2 py-1.5 text-sm text-gray-900 border border-transparent rounded hover:border-gray-200 focus:border-gray-300 focus:outline-none"
                      />
                      {renamed && (
                        <p
                          className="px-2 text-xs text-gray-400"
                          title={t('lotsPage.renamedHint')}
                        >
                          ← {lot.originalName}
                        </p>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <MarkdownField
                        value={lot.note ?? ''}
                        onChange={(value) =>
                          handleChange(lot.id, { note: value })
                        }
                        placeholder={t('lotsPage.notePlaceholder')}
                        minRows={1}
                        borderless
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <button
                        onClick={() => handleRemove(lot.id)}
                        className="text-gray-300 hover:text-red-500 transition-colors px-2 py-1.5"
                        title={t('lotsPage.removeLot')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-2 max-w-md">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              // IME変換確定のEnterでは追加しない（確定文字列が入力欄に残るのを防ぐ）
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                handleAdd()
              }
            }}
            placeholder={t('lotsPage.namePlaceholder')}
            className={inputClass}
          />
          <button
            onClick={handleAdd}
            disabled={!newName.trim()}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            <Plus className="h-4 w-4" />
            {t('lotsPage.addLot')}
          </button>
        </div>
      </section>

      {/* 削除はマスタから消えるだけ（機材側の値は残る）だが、念のため確認する */}
      <ConfirmDialog
        open={confirmOpen}
        title={t('lotsPage.confirmTitle')}
        message={t('lotsPage.confirmMessage', {
          names: removedNames.join('、'),
        })}
        confirmLabel={t('detail.save')}
        danger
        busy={saveMutation.isPending}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => saveMutation.mutate()}
      />
    </div>
  )
}
